import styled, { keyframes, createGlobalStyle, css } from "styled-components";

// Couleurs et typographie
const primaryColor = "#15b5b0";
const secondaryColor = "#f7f7f7";
const textColor = "#333";

const HEADER = 72;

// Keyframes pour les animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const HomePageContainer = styled.div`
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  color: ${textColor};
  animation: ${fadeIn} 1s ease-in-out;
  overflow-x: hidden;
`;

export const Header = styled.h1`
  font-family: "Cursive Dumont", cursive;
  color: ${primaryColor};
  text-align: center;
`;

export const IntroSection = styled.section`
  display: flex;
  align-items: center;
  justify-content: center; /* Centrer horizontalement */
  margin: 20px;
  gap: 20px; /* Espacement entre l'image et le texte */

  .intro-logo {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;

    img {
      width: 250px; /* Augmenter la taille de l'image */
      height: auto;
      border-radius: 8px; /* Arrondir les coins */
    }
  }

  .intro-text {
    flex: 2;
    background-color: ${secondaryColor}; /* Fond blanc uniquement pour le texte */
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1); /* Légère ombre */
    line-height: 1.6;
    animation: ${fadeIn} 1s ease-in-out;
  }

  @media (max-width: 768px) {
    flex-direction: column; /* Passer en vertical sur mobile */
    text-align: center;

    .intro-logo {
      margin-bottom: 15px;
    }
  }

  @media (max-width: 768px) {
    padding: 0px 24px 0px 24px;
  }
`;

export const TeamSection = styled.section`
  margin: 20px;
  text-align: center;

  h2 {
    color: ${primaryColor};
    font-family: "Cursive Dumont", cursive;
  }
`;

// Dans le fichier de styles de HomePage (ex: HomePage/styles.ts)
export const TeamStructure = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  width: 100%;

  /* Les lignes de l'équipe */
  .team-row {
    display: flex;
    justify-content: center;
    gap: 20px;
    width: 100%;
  }

  /* Gestion spéciale de la première ligne (Océane & Christine) */
  .team-row:first-child {
    display: flex;
    flex-direction: row; /* Par défaut : horizontal */
    justify-content: center;
    gap: 40px;
  }

  /* 🛠️ Média query pour mettre chaque membre sur une ligne en mobile */
  @media (max-width: 768px) {
    .team-row {
      flex-direction: column; /* Change les lignes en colonnes */
      align-items: center;
    }

    .team-row:first-child {
      flex-direction: column; /* Force Christine sous Océane */
      gap: 20px;
    }
  }
`;

export const TeamMember = styled.div`
  position: relative;
  text-align: center;
  margin: 15px;
  flex: 1 1 200px;
  cursor: pointer;
  animation: ${fadeIn} 1s ease-in-out;
  overflow: hidden; /* Pour éviter que l'overlay déborde */

  img {
    width: 120px;
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    transition: transform 0.3s ease-in-out;
  }

  p {
    font-weight: bold;
    margin-top: 10px;
  }

  span {
    color: gray;
    font-size: 0.9em;
  }

  /* 🛠️ En mobile, chaque profil prend toute la largeur */
  @media (max-width: 768px) {
    width: 100%;
    padding-bottom: 30px;
  }
`;

export const WelcomeSection = styled.section`
  margin: 20px;
  text-align: center;
  width: 100%;

  h2 {
    color: ${primaryColor};
    font-family: "Cursive Dumont", cursive;
  }

  p {
    text-align: center;
  }

  @media (max-width: 768px) {
    margin: 0;
  }
`;

export const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 20px; /* ✅ Évite que le popup touche les bords sur mobile */

  @media (max-width: 600px) {
    align-items: flex-start; /* ✅ Centre mieux sur petit écran */
    padding-top: 50px;
  }
`;

export const PopupContent = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  width: 90%;
  max-width: 450px;
  text-align: center;
  position: relative;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.2);

  button {
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }

  img {
    width: 120px; /* Taille adaptée pour éviter les distorsions */
    height: 120px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 10px;
  }

  h3 {
    font-size: 1.4rem;
    font-weight: bold;
    margin: 10px 0;
  }

  h4 {
    font-size: 1.1rem;
    color: gray;
    margin-bottom: 10px;
  }

  p {
    font-size: 1rem;
    color: #444;
    text-align: justify;
    line-height: 1.6;
    margin-top: 15px;
    padding: 0 10px;
  }

  @media (max-width: 600px) {
    width: 95%; /* Plus large sur mobile */
    max-width: 350px;
    top: 30%;
    left: -5%;

    img {
      width: 100px;
      height: 100px;
    }

    h3 {
      font-size: 1.2rem;
    }

    h4 {
      font-size: 1rem;
    }

    p {
      font-size: 0.95rem;
    }
  }
`;

const slideRtl = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); } /* on a dupliqué le contenu -> 50% = une largeur de liste */
`;

export const FacesMarqueeWrapper = styled.div`
  width: 100%;
  overflow: hidden;
  position: relative;
  padding: 16px 0;
`;

export const FacesTrack = styled.div`
  display: flex;
  gap: 28px;
  width: max-content;
  /* 2 listes à la suite -> on les anime ensemble */
  animation: ${slideRtl} 28s linear infinite;

  /* pause au survol */
  ${FacesMarqueeWrapper}:hover & {
    animation-play-state: paused;
  }
`;

export const FaceItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 120px;
  user-select: none;
  pointer-events: auto;

  img {
    width: 96px;
    height: 96px;
    border-radius: 9999px;
    object-fit: cover;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.08);
  }

  p {
    margin-top: 8px;
    font-weight: 600;
  }

  span {
    font-size: 0.9rem;
    color: #666;
    text-align: center;
  }
`;

/* Optionnel : fondu léger sur les bords */
export const EdgeFade = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 80px;
  pointer-events: none;
  z-index: 1;
  &.left {
    left: 0;
    background: linear-gradient(
      to right,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }
  &.right {
    right: 0;
    background: linear-gradient(
      to left,
      rgba(255, 255, 255, 1),
      rgba(255, 255, 255, 0)
    );
  }
`;

// === Boutons du hero
export const HeroButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-top: 14px;
`;

export const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 999px;
  font-weight: 600;
  text-decoration: none;
  border: 2px solid ${primaryColor};
  transition: transform 0.08s ease, box-shadow 0.2s ease, background 0.2s ease,
    color 0.2s ease;

  &.primary {
    background: ${primaryColor};
    color: #fff;
    box-shadow: 0 6px 18px rgba(21, 181, 176, 0.25);
  }
  &.ghost {
    background: transparent;
    color: ${primaryColor};
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(21, 181, 176, 0.28);
  }
  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

// === KPIs (faits clés)
export const HeroKpis = styled.div`
  grid-row: 3;
  grid-column: 1 / -1;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 4px; /* <= avant 14px ou mets 0 */
  @media (max-width: 1023px) {
    grid-template-columns: repeat(2, 1fr);
    margin-top: 6px;
  }
`;

export const KpiCard = styled.div`
  background: #fff;
  border-radius: 14px;
  padding: 10px 12px;
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.06);
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 10px;

  .icon {
    width: 40px;
    height: 40px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    background: rgba(21, 181, 176, 0.1);
    font-size: 20px;
  }
  strong {
    display: block;
    font-weight: 700;
    line-height: 1.1;
  }
  small {
    display: block;
    color: #666;
    line-height: 1.15;
  }
`;

export const SwiperGlobalStyle = createGlobalStyle`
  .swiper{
    position: relative;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
    padding-inline: 25px;   /* équivaut à left+right */
    min-height: 320px;
    overflow: hidden;
    margin-inline: auto;    /* centre dans son conteneur */
  }

  /* Mobile: retire le padding pour que ça soit vraiment centré */
  @media (max-width: 768px){
    .swiper{
      padding-inline: 0 !important;
    }
  }

  .swiper-slide{
    display:flex;
    align-items:center;
    justify-content:center;
    text-align:center;
  }

  .swiper-slide img{
    display:block;
    width:100%;
    height:360px;
    object-fit: cover;
    border-radius: 12px;
  }

  /* 📱 Ajuste l'image en mobile */
  @media (max-width: 768px) {
    .swiper-slide img{
      width: 100%;
      height: clamp(200px, 62vw, 300px);
      object-fit: cover;
      border-radius: 12px;
      margin: 0 auto;
      max-width: none;
    }
  }

  .swiper-button-prev, .swiper-button-next{
    color:#333;
    opacity:1;
    z-index:5;
  }

  .swiper-pagination-bullet { background:#333; }
  .swiper-scrollbar { background: rgba(0,0,0,.1); }
  .swiper-scrollbar-drag { background: rgba(0,0,0,.4); }

  @media (max-width: 480px){
    .mySwiper .slide-card{ max-width: 320px; }
  }

  /* ===== Bloc mobile corrigé ===== */
  @media (max-width: 768px){
    .mySwiper{
      /* taille et offset des flèches */
      --swiper-navigation-size: 18px;
      --swiper-navigation-sides-offset: 8px;
      padding: 0 !important;     /* <-- fini le décalage */
      margin: 0 auto;            /* centre le carrousel */
    }

    .mySwiper .swiper-button-prev,
    .mySwiper .swiper-button-next{
      display: flex !important;
      visibility: visible !important;
      opacity: 1 !important;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      width: 36px;
      height: 36px;
      background: rgba(255,255,255,0.95);
      border-radius: 50%;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0,0,0,.18);
      z-index: 50;
      pointer-events: auto;
    }

    /* utilise l’offset au lieu d’un padding conteneur */
    .mySwiper .swiper-button-prev { left: var(--swiper-navigation-sides-offset); }
    .mySwiper .swiper-button-next { right: var(--swiper-navigation-sides-offset); }

    /* au cas où un reset CSS supprimerait le ::after */
    .mySwiper .swiper-button-prev:after{
      font-family: 'swiper-icons';
      content: 'prev';
      font-size: 18px;
      color: #222;
    }
    .mySwiper .swiper-button-next:after{
      font-family: 'swiper-icons';
      content: 'next';
      font-size: 18px;
      color: #222;
    }
  }
  /* ===== fin bloc mobile ===== */

  /* 👉 Carte spéciale qui contient 3 images */
  .mySwiper .triple-slide{
    width:100%;
    max-width: 1100px; /* optionnel */
    margin: 0 auto;
  }

  .mySwiper .triple-slide .image-grid{
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
    width: 100%;
  }

  .mySwiper .triple-slide .image-grid img{
    width: 100%;
    height: 260px;
    object-fit: cover;
    border-radius: 12px;
    display: block;
  }

  .mySwiper .triple-slide figcaption{
    margin-top: 12px;
  }

  @media (max-width: 1024px){
    .mySwiper .triple-slide .image-grid{
      grid-template-columns: repeat(2, 1fr);
    }
    .mySwiper .triple-slide .image-grid img{
      height: 220px;
    }
  }

  @media (max-width: 640px){
    .mySwiper .triple-slide .image-grid{
      grid-template-columns: 1fr;
    }
    .mySwiper .triple-slide .image-grid img{
      height: 200px;
    }
  }

  /* Captions “Espace de vie” */
  @media (max-width: 768px) {
    .mySwiper .slide-card.espaceVie figcaption {
      display: block;
    }
  }

  @media (min-width: 769px) {
    .mySwiper .slide-card.espaceVie:not(.primary) figcaption {
      display: none;
    }
  }

  .mySwiper .espaceVie figcaption { display: none; }

  @media (min-width: 769px) {
    .mySwiper .espaceVie.primary figcaption { display: block; }
  }

  @media (max-width: 768px) {
    .mySwiper .espaceVie figcaption { display: block; }
  }
`;

/* Nouveau : le conteneur qui scroll + snap sous le navbar */
export const SnapContainer = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  overscroll-behavior: contain;

  /* Mobile: normal flow (no forced 100vh pages) */
  @media (max-width: 1023px) {
    scroll-snap-type: none;
  }
`;

/* Mixin : une section = une “page” en desktop */
const snapSection = css`
  min-height: 100vh;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  padding: 64px clamp(16px, 4vw, 32px);

  @media (max-width: 1023px) {
    min-height: auto;
    scroll-snap-align: unset;
    scroll-snap-stop: unset;
  }
`;

export const IntroSectionFS = styled(IntroSection)`
  ${snapSection};
  box-sizing: border-box;

  /* Desktop/tablette */
  min-height: calc(100dvh - ${HEADER}px);
  width: 92vw;
  max-width: 1400px;
  margin-inline: auto;

  display: grid;
  grid-template-columns: 4fr 8fr;
  grid-template-rows: auto 1fr auto; /* Titre / (Logo+Texte) / KPIs */
  column-gap: 48px;
  row-gap: 24px;
  align-items: center;

  > ${Header} {
    grid-row: 1;
    grid-column: 1 / -1;
    margin: 0;
    text-align: center;
  }

  .intro-logo {
    grid-row: 2;
    grid-column: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .intro-text {
    grid-row: 2;
    grid-column: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  ${HeroKpis} {
    grid-row: 3;
    grid-column: 1 / -1;
    margin-top: 12px;
  }

  /* 📱 Mobile : 1 seule colonne (titre → logo → texte → KPIs) */
  @media (max-width: 768px) {
    min-height: auto;
    max-height: none;
    width: 100%;
    max-width: 100%;
    padding: 16px 16px 24px;

    grid-template-columns: 1fr;
    grid-template-rows: auto auto auto auto;
    column-gap: 0;
    row-gap: 16px;

    > ${Header} {
      grid-row: 1;
      grid-column: 1;
      text-align: center;
    }

    .intro-logo {
      grid-row: 2;
      grid-column: 1;
      margin: 4px 0 8px;
    }
    .intro-logo img {
      max-width: 220px;
      width: 70%;
      height: auto;
    }

    .intro-text {
      grid-row: 3;
      grid-column: 1;
      justify-content: flex-start;
    }

    ${HeroKpis} {
      grid-row: 4;
      grid-column: 1;
      margin-top: 8px;
    }
  }
`;

export const TeamSectionFS = styled(TeamSection)`
  ${snapSection};
  min-height: calc(100dvh - ${HEADER}px);

  @media (max-width: 1023px) {
    min-height: auto;
  }
`;

export const WelcomeSectionFS = styled(WelcomeSection)`
  ${snapSection};
  min-height: calc(100dvh - ${HEADER}px);

  @media (max-width: 1023px) {
    min-height: auto;
    padding: 0;
  }
`;

export const FooterSection = styled.div`
  flex: 0 0 auto;
  width: 100%;
  scroll-snap-align: end;
  padding-top: 24px;

  /* Pas de snap forcé sur mobile */
  @media (max-width: 1023px) {
    scroll-snap-align: none;
  }
`;
